import { useQueryClient } from "@tanstack/react-query";

import { $api, type components, useIdempotencyKey } from "@/shared/api";

import { MATCH_LIST_QUERY_KEY } from "./MATCH_LIST_QUERY_KEY";
import { matchQueryOptions } from "./matchQueryOptions";

type AnalysisOut = components["schemas"]["AnalysisOut"];

type UseStartAnalysisOptions = {
  // Что сделать с созданным (или уже существующим) разбором: список матчей переходит
  // на страницу разбора, экран разбора кладёт разбор в свой кэш.
  onStarted?: (analysis: AnalysisOut) => unknown;
};

// Запуск разбора матча (`POST /api/analyses`). Ключ идемпотентности — один на матч,
// пока сервер не дал окончательного ответа: повторное нажатие после обрыва сети вернёт
// уже созданный разбор, а не создаст второй. После успеха перечитываются список матчей
// и сам матч: у них меняется `analysis_id`.
export const useStartAnalysis = ({ onStarted }: UseStartAnalysisOptions = {}) => {
  const idempotency = useIdempotencyKey();
  const queryClient = useQueryClient();
  const mutation = $api.useMutation("post", "/api/analyses", {
    onSettled: (_data, error) => {
      idempotency.settle(error);
    },
    onSuccess: async (analysis) => {
      await onStarted?.(analysis);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: matchQueryOptions(analysis.match_id).queryKey }),
        queryClient.invalidateQueries({ queryKey: MATCH_LIST_QUERY_KEY }),
      ]);
    },
  });
  const start = (matchId: number) => {
    mutation.mutate({
      body: { match_id: matchId },
      params: { header: { "Idempotency-Key": idempotency.keyFor(`match:${matchId}`) } },
    });
  };
  // Матч, для которого запрос сейчас в работе: его кнопка показывает «Запускаем…».
  const pendingMatchId = mutation.isPending ? mutation.variables.body.match_id : null;
  // Матч, разбор которого не запустился: ошибка показывается в его строке, рядом с кнопкой.
  const failedMatchId = mutation.isError ? mutation.variables.body.match_id : null;
  return { ...mutation, start, pendingMatchId, failedMatchId };
};
